import { isQaModeAllowedHost } from "@/qa/hostAllowlist";

/**
 * QA-only, non-Production fixtures for visually exercising every
 * `ScoreChallengeScene` landing state without a real challenge link, a
 * real second player, or any real network/ranked data - consistent with
 * this codebase's existing QA architecture (`src/qa/hostAllowlist.ts`,
 * `src/daily/dailyQa.ts`): gated by the exact same strict hostname
 * allowlist (local dev / this project's own Deploy Preview pattern), and
 * unreachable on production by construction - every getter here checks the
 * host first, the same "the host gate is inseparable from reading the
 * value" rule those modules already follow.
 *
 * A QA fixture never calls `ScoreChallengeClient`/`DailyChallengeClient`,
 * never calls `associate_daily_challenge_source`, and never fires a real
 * `score_challenge_*` PostHog conversion event - `ScoreChallengeScene`
 * checks `isQaFixture` before any of those, the same way a QA Daily
 * preview never calls `daily-attempt`/`daily-result` (see
 * `src/daily/qaPreview.ts`).
 */
export type ScoreChallengeQaPresetId = "active" | "expired" | "invalid" | "self" | "beat" | "tie" | "loss" | "exhausted_no_result";

export const SCORE_CHALLENGE_QA_PRESETS: readonly ScoreChallengeQaPresetId[] = ["active", "expired", "invalid", "self", "beat", "tie", "loss", "exhausted_no_result"];

export interface ScoreChallengeQaFixture {
  status: "active" | "expired" | "invalid" | "self";
  resolved?: {
    targetDailyPoints: number;
    creatorDisplayName: string;
    expiresAtMs: number;
    attemptsRemaining: number;
    hasResult: boolean;
    dailyPoints: number | null;
  };
}

export function isScoreChallengeQaAllowedHost(hostname: string | null | undefined): boolean {
  return isQaModeAllowedHost(hostname);
}

const FIXTURE_TARGET = 1842;
const FOUR_HOURS_MS = 4 * 60 * 60 * 1000;

/** Builds the fixture for one preset - pure, deterministic, and only ever consumed after the caller has already checked `isScoreChallengeQaAllowedHost` itself. */
export function buildScoreChallengeQaFixture(preset: ScoreChallengeQaPresetId, nowMs: number): ScoreChallengeQaFixture {
  const expiresAtMs = nowMs + FOUR_HOURS_MS;
  const base = { targetDailyPoints: FIXTURE_TARGET, creatorDisplayName: "ALMOG", expiresAtMs };

  switch (preset) {
    case "invalid":
      return { status: "invalid" };
    case "expired":
      return { status: "expired" };
    case "self":
      return { status: "self" };
    case "active":
      return { status: "active", resolved: { ...base, attemptsRemaining: 3, hasResult: false, dailyPoints: null } };
    case "beat":
      return { status: "active", resolved: { ...base, attemptsRemaining: 2, hasResult: true, dailyPoints: FIXTURE_TARGET + 171 } };
    case "tie":
      return { status: "active", resolved: { ...base, attemptsRemaining: 1, hasResult: true, dailyPoints: FIXTURE_TARGET } };
    case "loss":
      return { status: "active", resolved: { ...base, attemptsRemaining: 1, hasResult: true, dailyPoints: FIXTURE_TARGET - 229 } };
    case "exhausted_no_result":
      return { status: "active", resolved: { ...base, attemptsRemaining: 0, hasResult: false, dailyPoints: null } };
  }
}
